import { Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type ElementType } from 'react';
import { Footer, SEO } from '@/components';
import {
  FiDollarSign,
  FiMessageCircle,
  FiFileText,
  FiCalendar,
  FiShield,
  FiLock,
  FiBriefcase,
  FiMonitor,
  FiDownload,
  FiBookOpen,
  FiMenu,
  FiX,
} from 'react-icons/fi';
import { getPricingFeatures, type PricingFeature } from '@/lib/pricing-features';
import { setUserProperties } from '@/lib/analytics';
import {
  getAppStoreUrl,
  getPlayStoreUrl,
  getUtmParamsFromUrl,
  type UtmParams,
} from '@/lib/urls';
import {
  createLandingTracking,
  type LandingTracking,
} from '@/lib/landing-tracking';
import {
  LANDING_EXPERIMENT_KEY,
  landingExperimentProps,
} from '@/lib/landing-experiment';
import { landingSeoProps } from '@/lib/landing-seo';
import { FaApple, FaGooglePlay, FaGoogle } from 'react-icons/fa';
import { SiMicrosoftoutlook } from 'react-icons/si';
import shipSvg from '@/assets/ship.svg';
import woman1Png from '@/assets/woman_1.png';
import man1Png from '@/assets/man_1.png';
import { HeroMotionDemo } from '@/components/motion/HeroMotionDemo';
import { CalendarMotionDemo } from '@/components/motion/CalendarMotionDemo';
import { ExpensesMotionDemo } from '@/components/motion/ExpensesMotionDemo';
import { TemperatureCheckMotionDemo } from '@/components/motion/TemperatureCheckMotionDemo';
import { JournalMotionDemo } from '@/components/motion/JournalMotionDemo';
import { ReceiptScanMotionDemo } from '@/components/motion/ReceiptScanMotionDemo';
import { ExportsMotionDemo } from '@/components/motion/ExportsMotionDemo';
import { LaptopMotionDemo } from '@/components/motion/LaptopMotionDemo';

const KHULA =
  "'Khula', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
const NUNITO =
  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

// Hero Section
function HeroSection({
  trackingFunctions,
  utm,
}: {
  trackingFunctions: LandingTracking;
  utm: UtmParams;
}) {
  return (
    <section className='relative bg-primary pt-24 overflow-x-hidden md:max-h-[1200px] md:overflow-hidden'>
      {/* Waves Container - Full Width - Hidden on mobile, visible on desktop */}
      <div className='hidden md:block absolute left-0 right-0 top-[45%] bottom-0 pointer-events-none z-0'>
        <svg
          viewBox='0 0 1000 800'
          className='w-full h-full'
          preserveAspectRatio='none'
        >
          <defs>
            <linearGradient id='motionWaveGradient' x1='0%' y1='0%' x2='0%' y2='100%'>
              <stop offset='0%' stopColor='rgba(255, 255, 255, 0.2)' />
              <stop offset='100%' stopColor='rgba(255, 255, 255, 0.05)' />
            </linearGradient>
          </defs>
          <path
            d='M 0,120 Q 125,90 250,120 T 500,120 T 750,120 T 1000,120 L 1000,800 L 0,800 Z'
            fill='url(#motionWaveGradient)'
            opacity='0.4'
          />
          <path
            d='M 0,120 Q 125,90 250,120 T 500,120 T 750,120 T 1000,120'
            fill='none'
            stroke='rgba(255, 255, 255, 0.25)'
            strokeWidth='3'
            opacity='0.5'
          />
          <path
            d='M 0,140 Q 150,110 300,140 T 600,140 T 900,140 T 1000,140 L 1000,800 L 0,800 Z'
            fill='rgba(255, 255, 255, 0.08)'
            opacity='0.25'
          />
        </svg>
      </div>

      <div className='max-w-7xl mx-auto px-6 pt-2 pb-16 md:py-16 lg:py-24 relative z-10'>
        <div className='grid lg:grid-cols-2 gap-2 md:gap-6 lg:gap-12 items-start lg:items-center'>
          {/* Left Content - Text */}
          <div className='relative z-10 order-2 lg:order-1 -mt-4 lg:mt-0'>
            <h1
              className='text-5xl md:text-6xl lg:text-7xl font-light text-white leading-tight mb-6'
              style={{ fontFamily: KHULA, letterSpacing: '-0.02em' }}
            >
              Co-parenting
              <br />
              made <span className='text-cream'>easy.</span>
            </h1>
            <p
              className='text-xl text-white/80 max-w-md mb-8'
              style={{ fontFamily: NUNITO, fontWeight: 400 }}
            >
              What the $300-a-year apps charge for, only $49.99 a year. Works
              solo or with both parents.
            </p>
            <div className='flex flex-col sm:flex-row gap-4'>
              <a
                href={getAppStoreUrl('website-hero', utm)}
                target='_blank'
                rel='noopener noreferrer'
                onClick={() => trackingFunctions.handleAppStoreClick('Hero')}
                className='inline-flex items-center justify-center gap-2 bg-cream hover:bg-cream-dark text-primary font-semibold px-8 py-4 rounded-lg transition-all hover:shadow-xl hover:scale-105 text-lg w-full sm:flex-1'
                style={{ fontFamily: NUNITO, fontWeight: 600 }}
              >
                <FaApple size={20} />
                <span>App Store</span>
              </a>
              <a
                href={getPlayStoreUrl('website-hero', utm)}
                target='_blank'
                rel='noopener noreferrer'
                onClick={() => trackingFunctions.handleGooglePlayClick('Hero')}
                className='inline-flex items-center justify-center gap-2 bg-cream hover:bg-cream-dark text-primary font-semibold px-8 py-4 rounded-lg transition-all hover:shadow-xl hover:scale-105 text-lg w-full sm:flex-1'
                style={{ fontFamily: NUNITO, fontWeight: 600 }}
              >
                <FaGooglePlay size={20} />
                <span>Google Play</span>
              </a>
            </div>
          </div>

          {/* Right Content - Hero Demo */}
          <div className='relative z-20 flex items-center justify-center order-1 lg:order-2 overflow-visible'>
            <HeroMotionDemo />
          </div>
        </div>
      </div>

      {/* Bottom wave/curve transition — keep behind hero devices */}
      <div className='absolute bottom-0 left-0 right-0 z-0 pointer-events-none'>
        <svg
          viewBox='0 0 1440 120'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          className='w-full h-auto'
          preserveAspectRatio='none'
        >
          <path
            d='M0 120V60C240 20 480 0 720 0C960 0 1200 20 1440 60V120H0Z'
            fill='#dbeef2'
          />
        </svg>
      </div>
    </section>
  );
}

interface Feature {
  icon: ElementType;
  title: string;
  description: string;
  demo: ElementType;
  integrations?: { icon: ElementType; label: string }[];
}

// Feature Display Component
function FeatureDisplay({
  feature,
  imageOnRight,
}: {
  feature: Feature;
  imageOnRight: boolean;
}) {
  const Icon = feature.icon;
  const Demo = feature.demo;

  return (
    <div className='grid lg:grid-cols-2 gap-12 items-center'>
      {/* Feature Demo - Always first on mobile, alternating on desktop */}
      <div
        className={`relative flex items-center justify-center order-1 ${
          imageOnRight ? 'lg:order-2' : 'lg:order-1'
        } max-w-lg`}
      >
        <Demo />
      </div>

      {/* Feature Info - Always second on mobile, alternating on desktop */}
      <div
        className={`order-2 ${
          imageOnRight ? 'lg:order-1' : 'lg:order-2'
        } max-w-lg`}
      >
        <div className='w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6'>
          <Icon className='w-7 h-7 text-primary' />
        </div>
        <h3
          className='text-3xl md:text-4xl font-bold text-gray-900 mb-4'
          style={{ fontFamily: NUNITO, fontWeight: 700 }}
        >
          {feature.title}
        </h3>
        <p
          className='text-lg text-gray-600 leading-relaxed'
          style={{ fontFamily: NUNITO, fontWeight: 400 }}
        >
          {feature.description}
        </p>
        {feature.integrations && (
          <div className='mt-5'>
            <p
              className='text-xs font-medium text-gray-400 uppercase tracking-wide mb-2'
              style={{ fontFamily: NUNITO }}
            >
              Syncs with
            </p>
            <div className='flex flex-wrap gap-2'>
              {feature.integrations.map((integration) => {
                const IntegrationIcon = integration.icon;
                return (
                  <div
                    key={integration.label}
                    className='flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full border border-gray-200 shadow-sm'
                  >
                    <IntegrationIcon className='w-3.5 h-3.5 text-gray-600' />
                    <span
                      className='text-xs font-medium text-gray-600'
                      style={{ fontFamily: NUNITO }}
                    >
                      {integration.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Features Section
function FeaturesSection() {
  const features: Feature[] = [
    {
      icon: FiDollarSign,
      title: 'Expense Tracking',
      description:
        'He says he covered the cleats. Did he? Log it, attach the receipt, split it, and request the reimbursement. The record settles it, not the argument.',
      demo: ExpensesMotionDemo,
    },
    {
      icon: FiCalendar,
      title: 'Custody Calendar',
      description:
        "One custody calendar you both see, with swaps and exceptions written down. Syncs to Google, Apple, and Outlook so it's on the phone you already check.",
      demo: CalendarMotionDemo,
      integrations: [
        { icon: FaGoogle, label: 'Google Calendar' },
        { icon: FaApple, label: 'Apple Calendar' },
        { icon: SiMicrosoftoutlook, label: 'Outlook' },
      ],
    },
    {
      icon: FiMessageCircle,
      title: 'Secure Messaging',
      description:
        "No deleted texts, no 'I never said that.' Messages are timestamped and kept. Optional Temperature Check flags a heated draft before you send it.",
      demo: TemperatureCheckMotionDemo,
    },
    {
      icon: FiBookOpen,
      title: 'Private Journal',
      description:
        "Late pickups, the doctor's visit, what was said at handoff. A private, timestamped notebook your co-parent never sees, so you're not reconstructing it months later.",
      demo: JournalMotionDemo,
    },
    {
      icon: FiFileText,
      title: 'Receipt Scanner',
      description:
        'Photograph it and Harbor pulls the amount, merchant, and date and pre-fills the expense. No spreadsheet, no shoebox.',
      demo: ReceiptScanMotionDemo,
    },
    {
      icon: FiDownload,
      title: 'Court-Ready Exports',
      description:
        'Export messages, expenses, or the calendar to a clean report in your inbox in minutes, not a weekend of screenshots.',
      demo: ExportsMotionDemo,
    },
  ];

  const webAppFeature = {
    icon: FiMonitor,
    title: 'Web App',
    description:
      "Full web app for when you're at a desk. Same account, same records.",
  };

  return (
    <section id='features' className='bg-sky-light py-24'>
      <div className='max-w-7xl mx-auto px-6'>
        {/* Section Header */}
        <div className='text-center mb-16 relative'>
          <div>
            <div className='flex justify-center mb-6'>
              <div className='bg-primary rounded-full p-4 md:p-5 lg:p-6 flex items-center justify-center aspect-square'>
                <img
                  src={shipSvg}
                  alt='Sailboat'
                  className='w-12 h-auto md:w-14 lg:w-16'
                  loading='lazy'
                />
              </div>
            </div>
            <span
              className='text-primary font-semibold text-lg mb-2 block'
              style={{ fontFamily: NUNITO, fontWeight: 600 }}
            >
              All-In-One
            </span>
            <h2
              className='text-4xl md:text-5xl font-bold text-gray-900 mb-4'
              style={{ fontFamily: NUNITO, fontWeight: 700 }}
            >
              Tools That Keep You Aligned
            </h2>
            <p
              className='text-xl text-gray-600 max-w-2xl mx-auto'
              style={{ fontFamily: NUNITO, fontWeight: 400 }}
            >
              Everything co-parents need to manage finances, schedules, and
              communication in one place.
            </p>
          </div>
        </div>

        {/* Feature Displays - Alternating layout */}
        {features.map((feature, index) => (
          <div key={feature.title}>
            <FeatureDisplay feature={feature} imageOnRight={index % 2 === 0} />
            {index < features.length - 1 && (
              <div className='flex justify-center my-16'>
                <div className='border-t border-gray-300 w-full max-w-3xl' />
              </div>
            )}
          </div>
        ))}

        {/* Web App Feature - Vertical Layout */}
        <div className='flex justify-center my-16'>
          <div className='border-t border-gray-300 w-full max-w-3xl' />
        </div>
        <div className='flex flex-col items-center text-center max-w-4xl mx-auto'>
          <div className='w-full max-w-md lg:max-w-[450px] mb-8'>
            <LaptopMotionDemo />
          </div>
          <div>
            <div className='w-14 h-14 mx-auto bg-primary/10 rounded-xl flex items-center justify-center mb-6'>
              <FiMonitor className='w-7 h-7 text-primary' />
            </div>
            <h3
              className='text-3xl md:text-4xl font-bold text-gray-900 mb-4'
              style={{ fontFamily: NUNITO, fontWeight: 700 }}
            >
              {webAppFeature.title}
            </h3>
            <p
              className='text-lg text-gray-600 leading-relaxed max-w-2xl'
              style={{ fontFamily: NUNITO, fontWeight: 400 }}
            >
              {webAppFeature.description}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// Stats Section
function StatsSection() {
  const valueProps = [
    {
      icon: FiShield,
      title: 'Court-Ready Records',
      description:
        'Documentation that meets legal standards nationwide, ready whenever you need them in court.',
    },
    {
      icon: FiLock,
      title: 'Tamper-Proof Record',
      description:
        'All communications are permanently secured and every action is tracked in a tamper-proof audit trail.',
    },
    {
      icon: FiBriefcase,
      title: 'Works For Your Situation',
      description:
        'Harbor adapts to your unique custody arrangements and grows with your family over time.',
    },
  ];

  return (
    <section className='bg-primary py-20'>
      <div className='max-w-7xl mx-auto px-6'>
        <div className='grid md:grid-cols-3 gap-8 md:gap-12'>
          {valueProps.map((prop, index) => {
            const Icon = prop.icon;
            return (
              <div
                key={prop.title}
                className={`text-center ${
                  index < valueProps.length - 1
                    ? 'md:border-r md:border-white/20 pr-0 md:pr-8'
                    : ''
                }`}
              >
                <div className='w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4'>
                  <Icon className='w-8 h-8 text-white' />
                </div>
                <h3
                  className='text-xl md:text-2xl font-bold text-white mb-3'
                  style={{ fontFamily: NUNITO, fontWeight: 700 }}
                >
                  {prop.title}
                </h3>
                <p
                  className='text-white/90 leading-relaxed'
                  style={{ fontFamily: NUNITO, fontWeight: 400 }}
                >
                  {prop.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// Pricing Section
function PricingSection({
  trackingFunctions,
  utm,
}: {
  trackingFunctions: LandingTracking;
  utm: UtmParams;
}) {
  return (
    <section id='pricing' className='bg-white py-24'>
      <div className='max-w-7xl mx-auto px-6'>
        <div className='text-center mb-16'>
          <span
            className='text-primary font-semibold text-lg mb-2 block'
            style={{ fontFamily: NUNITO, fontWeight: 600 }}
          >
            No hidden fees
          </span>
          <h2
            className='text-4xl md:text-5xl font-bold text-gray-900 mb-4'
            style={{ fontFamily: NUNITO, fontWeight: 700 }}
          >
            Simple, Transparent Pricing
          </h2>
          <p
            className='text-xl text-gray-600 max-w-2xl mx-auto'
            style={{ fontFamily: NUNITO, fontWeight: 400 }}
          >
            Compare Harbor to other co-parenting apps and see the value.
          </p>
        </div>

        <div className='grid md:grid-cols-2 gap-8 max-w-4xl mx-auto'>
          {/* Other Apps */}
          <div>
            <div className='border-2 border-gray-200 rounded-2xl p-8'>
              <h3
                className='text-2xl font-bold text-gray-900 mb-2'
                style={{ fontFamily: NUNITO, fontWeight: 700 }}
              >
                Other Apps
              </h3>
              <div className='mb-4'>
                <div
                  className='text-4xl font-bold text-gray-900 mb-1'
                  style={{ fontFamily: NUNITO, fontWeight: 700 }}
                >
                  $24.99
                  <span
                    className='text-lg font-normal text-gray-500'
                    style={{ fontWeight: 400 }}
                  >
                    {' '}
                    / month
                  </span>
                </div>
                <div
                  className='text-2xl font-bold text-gray-700'
                  style={{ fontFamily: NUNITO, fontWeight: 700 }}
                >
                  $299.99
                  <span
                    className='text-base font-normal text-gray-500'
                    style={{ fontWeight: 400 }}
                  >
                    {' '}
                    / year
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Pro Plan */}
          <div>
            <div className='border-2 border-primary bg-primary/5 rounded-2xl p-8 relative'>
              <div
                className='absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-sm font-medium px-4 py-1 rounded-full'
                style={{ fontFamily: NUNITO, fontWeight: 400 }}
              >
                Harbor
              </div>
              <div className='mb-6'>
                <div
                  className='text-4xl font-bold text-gray-900 mb-1'
                  style={{ fontFamily: NUNITO, fontWeight: 700 }}
                >
                  $9.99
                  <span
                    className='text-lg font-normal text-gray-500'
                    style={{ fontWeight: 400 }}
                  >
                    {' '}
                    / month
                  </span>
                </div>
                <div
                  className='text-2xl font-bold text-gray-700'
                  style={{ fontFamily: NUNITO, fontWeight: 700 }}
                >
                  $49.99
                  <span
                    className='text-base font-normal text-gray-500'
                    style={{ fontWeight: 400 }}
                  >
                    {' '}
                    / year
                  </span>
                </div>
              </div>
              <ul className='space-y-4 mb-8'>
                {getPricingFeatures('main').map((feature: PricingFeature) => {
                  const Icon = feature.icon;
                  return (
                    <li key={feature.id} className='flex items-center gap-3'>
                      <Icon className='w-4 h-4 text-primary flex-shrink-0' />
                      <span
                        className='text-gray-600'
                        style={{ fontFamily: NUNITO, fontWeight: 400 }}
                      >
                        {feature.text}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <div className='flex flex-col gap-3'>
                <a
                  href={getAppStoreUrl('website-pricing', utm)}
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={() =>
                    trackingFunctions.handleAppStoreClick('Pricing')
                  }
                  className='flex items-center justify-center gap-2 w-full bg-primary hover:bg-primary-hover text-white font-semibold py-3 rounded-lg transition-colors'
                  style={{ fontFamily: NUNITO, fontWeight: 600 }}
                >
                  <FaApple size={20} />
                  <span>App Store</span>
                </a>
                <a
                  href={getPlayStoreUrl('website-pricing', utm)}
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={() =>
                    trackingFunctions.handleGooglePlayClick('Pricing')
                  }
                  className='flex items-center justify-center gap-2 w-full bg-primary hover:bg-primary-hover text-white font-semibold py-3 rounded-lg transition-colors'
                  style={{ fontFamily: NUNITO, fontWeight: 600 }}
                >
                  <FaGooglePlay size={20} />
                  <span>Google Play</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Testimonials Section
function TestimonialsSection() {
  const testimonials = [
    {
      quote: [
        { text: 'I used to ' },
        { text: 'dread every text! ', highlight: true },
        { text: "Now I don't feel sick checking my phone." },
      ],
      name: 'Justine M.',
      descriptor: 'Mom of 3 · Uses tone check',
      avatar: woman1Png,
    },
    {
      quote: [
        { text: 'Finally feels like I have a record on my side. The ' },
        { text: 'peace of mind', highlight: true },
        { text: ' is worth it!' },
      ],
      name: 'Josh C.',
      descriptor: 'Dad of 2 · Tracks expenses & custody',
      avatar: man1Png,
    },
  ];

  return (
    <section className='bg-white pt-16 pb-24'>
      <div className='max-w-5xl mx-auto px-6'>
        <div className='text-center mb-16'>
          <span
            className='text-primary font-semibold text-lg mb-2 block'
            style={{ fontFamily: NUNITO, fontWeight: 600 }}
          >
            Loved by parents
          </span>
          <h2
            className='text-4xl md:text-5xl font-bold text-gray-900 mb-4'
            style={{ fontFamily: NUNITO, fontWeight: 700 }}
          >
            Real parents, real peace of mind
          </h2>
        </div>

        <div className='grid md:grid-cols-2 gap-8'>
          {testimonials.map((testimonial) => (
            <div key={testimonial.name}>
              <div className='bg-sky-light rounded-2xl p-8 border border-gray-100 shadow-sm'>
                <p
                  className='text-xl text-gray-900 leading-relaxed'
                  style={{ fontFamily: NUNITO, fontWeight: 600 }}
                >
                  &ldquo;
                  {testimonial.quote.map((segment, segmentIndex) => (
                    <span
                      key={segmentIndex}
                      className={
                        segment.highlight ? 'text-primary font-extrabold' : ''
                      }
                    >
                      {segment.text}
                    </span>
                  ))}
                  &rdquo;
                </p>
                <div className='mt-6 flex items-center gap-3'>
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className='w-12 h-12 rounded-full object-cover'
                    loading='lazy'
                  />
                  <div>
                    <div
                      className='text-base font-bold text-gray-900'
                      style={{ fontFamily: NUNITO, fontWeight: 700 }}
                    >
                      {testimonial.name}
                    </div>
                    <div
                      className='text-sm text-gray-500'
                      style={{ fontFamily: NUNITO, fontWeight: 500 }}
                    >
                      {testimonial.descriptor}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// CTA Section
function CTASection({
  trackingFunctions,
  utm,
}: {
  trackingFunctions: LandingTracking;
  utm: UtmParams;
}) {
  return (
    <section className='bg-sky-light py-24'>
      <div className='max-w-4xl mx-auto px-6 text-center'>
        <div>
          <h2
            className='text-4xl md:text-5xl font-bold text-gray-900 mb-6'
            style={{ fontFamily: NUNITO, fontWeight: 700 }}
          >
            Ready to make co-parenting easier?
          </h2>
          <p
            className='text-xl text-gray-600 mb-8'
            style={{ fontFamily: NUNITO, fontWeight: 400 }}
          >
            Start with one parent. Invite the other whenever you&apos;re ready.
            Free for 7 days.
          </p>
          <div className='flex flex-col sm:flex-row gap-4 justify-center'>
            <a
              href={getAppStoreUrl('website-cta', utm)}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => trackingFunctions.handleAppStoreClick('CTA')}
              className='inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover text-white font-semibold px-8 py-4 rounded-lg transition-all hover:shadow-xl text-lg w-full sm:flex-1 sm:max-w-xs'
              style={{ fontFamily: NUNITO, fontWeight: 600 }}
            >
              <FaApple size={20} />
              <span>App Store</span>
            </a>
            <a
              href={getPlayStoreUrl('website-cta', utm)}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => trackingFunctions.handleGooglePlayClick('CTA')}
              className='inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover text-white font-semibold px-8 py-4 rounded-lg transition-all hover:shadow-xl text-lg w-full sm:flex-1 sm:max-w-xs'
              style={{ fontFamily: NUNITO, fontWeight: 600 }}
            >
              <FaGooglePlay size={20} />
              <span>Google Play</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// Landing navigation header, mirrored 1:1 from the live page.
function MirrorHeader({
  trackingFunctions,
}: {
  trackingFunctions: LandingTracking;
}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const linkStyle = { fontFamily: NUNITO, fontWeight: 400 } as const;

  return (
    <header className='fixed top-0 left-0 right-0 z-50 bg-primary/95 backdrop-blur-sm'>
      <nav className='max-w-7xl mx-auto px-6 py-4'>
        <div className='flex items-center justify-between'>
          <Link
            to='/'
            search={{ hero: undefined }}
            className='flex items-center gap-2 text-white text-2xl font-light tracking-tight leading-none'
            style={{ fontFamily: KHULA }}
          >
            <div className='flex items-center'>
              <img src={shipSvg} alt='' className='w-5 h-5 mr-3' />
              <span className='flex items-center mt-2'>Harbor</span>
            </div>
          </Link>

          <div className='hidden md:flex items-center gap-8'>
            <a
              href='#features'
              onClick={() => trackingFunctions.handleNavClick('Features')}
              className='text-white/90 hover:text-white transition-colors font-medium'
              style={linkStyle}
            >
              Features
            </a>
            <a
              href='#pricing'
              onClick={() => trackingFunctions.handleNavClick('Pricing')}
              className='text-white/90 hover:text-white transition-colors font-medium'
              style={linkStyle}
            >
              Pricing
            </a>
            <Link
              to='/auth/sign-in'
              onClick={trackingFunctions.handleLogInClick}
              className='text-white/90 hover:text-white transition-colors font-medium'
              style={linkStyle}
            >
              Log In
            </Link>
            <Link
              to='/auth/sign-up'
              onClick={trackingFunctions.handleSignUpClick}
              className='bg-white text-primary hover:bg-white/90 px-5 py-2 rounded-xl font-semibold transition-colors'
              style={{ fontFamily: NUNITO }}
            >
              Sign Up
            </Link>
          </div>

          <button
            className='md:hidden text-white p-2'
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className='md:hidden mt-4 pb-4 border-t border-white/20 pt-4 flex flex-col gap-4'>
            <a
              href='#features'
              onClick={() => {
                trackingFunctions.handleNavClick('Features');
                setMobileMenuOpen(false);
              }}
              className='text-white/90 hover:text-white font-medium'
              style={linkStyle}
            >
              Features
            </a>
            <a
              href='#pricing'
              onClick={() => {
                trackingFunctions.handleNavClick('Pricing');
                setMobileMenuOpen(false);
              }}
              className='text-white/90 hover:text-white font-medium'
              style={linkStyle}
            >
              Pricing
            </a>
            <div className='flex flex-col gap-3 mt-2 pt-4 border-t border-white/20'>
              <Link
                to='/auth/sign-in'
                onClick={trackingFunctions.handleLogInClick}
                className='text-white/90 hover:text-white font-medium'
                style={linkStyle}
              >
                Log In
              </Link>
              <Link
                to='/auth/sign-up'
                onClick={trackingFunctions.handleSignUpClick}
                className='bg-white text-primary hover:bg-white/90 px-5 py-2.5 rounded-xl font-semibold transition-colors text-center'
                style={{ fontFamily: NUNITO }}
              >
                Sign Up
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}

export function MotionLandingPage({
  mode,
}: {
  mode: 'experiment' | 'preview';
}) {
  const variant = mode === 'preview' ? 'preview' : 'motion';
  const trackingFunctions = useMemo(
    () => createLandingTracking({ variant }),
    [variant]
  );
  const [utm] = useState<UtmParams>(() => getUtmParamsFromUrl());

  useEffect(() => {
    trackingFunctions.trackLandingScreen();
    setUserProperties({
      landing_variant: variant,
      landing_experiment: LANDING_EXPERIMENT_KEY,
      ...landingExperimentProps(variant),
    });
  }, [trackingFunctions, variant]);

  return (
    <div className='min-h-screen'>
      {mode === 'preview' ? (
        <SEO
          title='Harbor — Motion Preview'
          description='Motion preview of the Harbor landing page with animated device demos.'
          path='/motion'
          noindex
        />
      ) : (
        <SEO {...landingSeoProps} />
      )}
      <MirrorHeader trackingFunctions={trackingFunctions} />
      <HeroSection trackingFunctions={trackingFunctions} utm={utm} />
      <FeaturesSection />
      <StatsSection />
      <PricingSection trackingFunctions={trackingFunctions} utm={utm} />
      <TestimonialsSection />
      <CTASection trackingFunctions={trackingFunctions} utm={utm} />
      <Footer />
    </div>
  );
}
